// Chemin: C:\gestion-copro\src\types\copropriete.types.ts
// Types pour la gestion des copropriétés - Copro Protect
// Session 40 : ajout onboarding, offre commerciale, administrateur désigné
// Session 59 : ajout sceauUrl et slug pour identité visuelle
// Session 81 : ajout champs quota SENTINELLE (sentinelleQuota, sentinelleUsage, sentinelleFormule)
// Session 88 : Gouvernance V2 — coordinateur remplace administrateur, statut onboarding aligné sur mandat

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// TYPES DE BASE
// ============================================================================

/**
 * Types de construction possibles pour une copropriété
 */
export type TypeConstruction = 'neuf_vefa' | 'neuf_ccmi' | 'ancien' | 'renove';

/**
 * Plans d'abonnement disponibles
 */
export type PlanAbonnement = 'essentiel' | 'pro' | 'premium';

/**
 * Statuts possibles d'un abonnement
 */
export type StatutAbonnement = 'actif' | 'expire' | 'suspendu';

/**
 * Formules SENTINELLE disponibles
 * Session 81 : ajout pour gestion quota IA
 */
export type FormuleSentinelle = 'SENTINELLE' | 'BOUCLIER' | 'FORTERESSE';

// ============================================================================
// ONBOARDING ET OFFRE COMMERCIALE
// ============================================================================

/**
 * Statut de l'onboarding d'une copropriété
 * Session 88 : aligné sur le workflow de mandat de coordination
 * 
 * - en_attente_signatures : mandat créé, membres CS n'ont pas tous signé
 * - operationnelle : mandat signé par tous, coordinateur mandaté
 * - suspendue : copropriété temporairement désactivée
 * 
 * Note rétrocompatibilité : 'en_attente_confirmations' est mappé vers 
 * 'en_attente_signatures' dans les helpers (anciennes données Firestore)
 */
export type StatutOnboarding = 'en_attente_signatures' | 'en_attente_confirmations' | 'operationnelle' | 'suspendue';

/**
 * Type d'offre commerciale
 * - autonome : le CS gère seul avec un coordinateur bénévole
 * - accompagnee : DOM TECH & SERVICES est le coordinateur
 */
export type TypeOffre = 'autonome' | 'accompagnee';

/**
 * Informations du prestataire (si offre accompagnée)
 */
export interface Prestataire {
  societe: string;           // "DOM TECH & SERVICES"
  siret: string;
  contactNom: string;        // "Dominique SERGENT"
  contactEmail: string;
  debutContrat: Timestamp;
  finContrat?: Timestamp;    // Optionnel si contrat en cours
}

// ============================================================================
// STRUCTURES EXISTANTES
// ============================================================================

/**
 * Adresse postale
 */
export interface Adresse {
  rue: string;
  codePostal: string;
  ville: string;
}

/**
 * Informations d'abonnement de la copropriété
 */
export interface Abonnement {
  plan: PlanAbonnement;
  dateDebut: Timestamp;
  dateFin: Timestamp;
  statut: StatutAbonnement;
}

// ============================================================================
// ENTITÉ PRINCIPALE
// ============================================================================

/**
 * Entité principale Copropriete
 * Représente une copropriété dans le système multi-tenant
 */
export interface Copropriete {
  id: string;
  nom: string;
  adresse: Adresse;
  nombreLots: number;
  tantiemesTotal: number;
  typeConstruction: TypeConstruction;
  
  // Dates clés pour le suivi des garanties (optionnelles car dépendent du type)
  dateReceptionTravaux?: Timestamp;
  dateExpirationGPA?: Timestamp;        // Garantie Parfait Achèvement (1 an)
  dateExpirationBiennale?: Timestamp;   // Garantie biennale (2 ans)
  dateExpirationDecennale?: Timestamp;  // Garantie décennale (10 ans)
  
  // Gestion de l'abonnement SaaS
  abonnement: Abonnement;
  
  // --- QUOTA SENTINELLE (Session 81) ---
  
  sentinelleFormule?: FormuleSentinelle;
  sentinelleQuota?: number;
  sentinelleUsage?: number;
  sentinelleRenouvellement?: Timestamp;
  
  // --- IDENTITÉ VISUELLE ---
  
  sceauUrl?: string;
  slug?: string;
  
  // --- COORDINATEUR (Session 88 - Gouvernance V2) ---
  
  /**
   * ID de l'utilisateur désigné comme Coordinateur
   * Remplace administrateurId (rétrocompatibilité assurée)
   */
  coordinateurId?: string;
  
  /**
   * Nom affiché du Coordinateur (pour éviter des lectures Firestore)
   */
  coordinateurNom?: string;
  
  /**
   * Le coordinateur est-il membre élu du CS ?
   * true = scénario A (80% des cas)
   * false = scénario B (tiers) ou C (DOM TECH & SERVICES)
   */
  coordinateurEstMembreCS?: boolean;
  
  /**
   * Date de désignation du Coordinateur
   */
  dateDesignationCoordinateur?: Timestamp;
  
  /**
   * ID du mandat de coordination actif
   * Référence vers coproprietes/{id}/mandats/{mandatId}
   */
  mandatActifId?: string;
  
  // --- RÉTROCOMPATIBILITÉ (Session 40 → Session 88) ---
  // Ces champs sont conservés pour ne pas casser le code existant
  // Ils seront supprimés après migration complète
  
  /** @deprecated Utiliser coordinateurId */
  administrateurId?: string;
  /** @deprecated Utiliser coordinateurNom */
  administrateurNom?: string;
  /** @deprecated Utiliser dateDesignationCoordinateur */
  dateDesignationAdmin?: Timestamp;
  
  // --- ONBOARDING ET OFFRE COMMERCIALE ---
  
  statutOnboarding?: StatutOnboarding;
  typeOffre?: TypeOffre;
  prestataire?: Prestataire;
  
  // --- FIN ---
  
  // Métadonnées
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
}

// ============================================================================
// DONNÉES POUR CRÉATION / MISE À JOUR
// ============================================================================

/**
 * Données pour la création d'une nouvelle copropriété
 */
export interface CoproprieteCreateData {
  nom: string;
  adresse: Adresse;
  nombreLots: number;
  tantiemesTotal: number;
  typeConstruction: TypeConstruction;
  dateReceptionTravaux?: Date;
  abonnement: {
    plan: PlanAbonnement;
    dateDebut: Date;
    dateFin: Date;
    statut: StatutAbonnement;
  };
  // Identité visuelle
  sceauUrl?: string;
  slug?: string;
  // Onboarding
  statutOnboarding?: StatutOnboarding;
  typeOffre?: TypeOffre;
  prestataire?: Omit<Prestataire, 'debutContrat' | 'finContrat'> & {
    debutContrat: Date;
    finContrat?: Date;
  };
  // Coordinateur (Session 88)
  coordinateurId?: string;
  coordinateurNom?: string;
  coordinateurEstMembreCS?: boolean;
}

/**
 * Données pour la mise à jour d'une copropriété
 */
export interface CoproprieteUpdateData {
  nom?: string;
  adresse?: Partial<Adresse>;
  nombreLots?: number;
  tantiemesTotal?: number;
  typeConstruction?: TypeConstruction;
  dateReceptionTravaux?: Date;
  dateExpirationGPA?: Date;
  dateExpirationBiennale?: Date;
  dateExpirationDecennale?: Date;
  abonnement?: Partial<Abonnement>;
  // Identité visuelle
  sceauUrl?: string;
  slug?: string;
  // Onboarding
  statutOnboarding?: StatutOnboarding;
  typeOffre?: TypeOffre;
  prestataire?: Partial<Prestataire>;
  // Coordinateur (Session 88)
  coordinateurId?: string;
  coordinateurNom?: string;
  coordinateurEstMembreCS?: boolean;
  dateDesignationCoordinateur?: Date;
  mandatActifId?: string;
  // Quota SENTINELLE (Session 81)
  sentinelleFormule?: FormuleSentinelle;
  sentinelleQuota?: number;
  sentinelleUsage?: number;
  sentinelleRenouvellement?: Date;
}

// ============================================================================
// LABELS ET CONSTANTES
// ============================================================================

export const STATUT_ONBOARDING_LABELS: Record<StatutOnboarding, string> = {
  en_attente_signatures: 'En attente des signatures',
  en_attente_confirmations: 'En attente des signatures',  // Alias rétrocompat
  operationnelle: 'Opérationnelle',
  suspendue: 'Suspendue'
};

export const STATUT_ONBOARDING_COLORS: Record<StatutOnboarding, string> = {
  en_attente_signatures: 'bg-amber-100 text-amber-800',
  en_attente_confirmations: 'bg-amber-100 text-amber-800',  // Alias rétrocompat
  operationnelle: 'bg-green-100 text-green-800',
  suspendue: 'bg-red-100 text-red-800'
};

export const TYPE_OFFRE_LABELS: Record<TypeOffre, string> = {
  autonome: 'Offre Autonome',
  accompagnee: 'Offre Accompagnée'
};

export const TYPE_OFFRE_DESCRIPTIONS: Record<TypeOffre, string> = {
  autonome: 'Le Conseil Syndical gère seul avec un coordinateur bénévole',
  accompagnee: 'DOM TECH & SERVICES pilote la plateforme pour vous'
};

export const TYPE_CONSTRUCTION_LABELS: Record<TypeConstruction, string> = {
  neuf_vefa: 'Neuf (VEFA)',
  neuf_ccmi: 'Neuf (CCMI)',
  ancien: 'Ancien',
  renove: 'Rénové'
};

export const PLAN_ABONNEMENT_LABELS: Record<PlanAbonnement, string> = {
  essentiel: 'Essentiel',
  pro: 'Pro',
  premium: 'Premium'
};

export const FORMULE_SENTINELLE_LABELS: Record<FormuleSentinelle, string> = {
  SENTINELLE: 'Sentinelle',
  BOUCLIER: 'Bouclier',
  FORTERESSE: 'Forteresse'
};

export const FORMULE_SENTINELLE_QUOTAS: Record<FormuleSentinelle, number> = {
  SENTINELLE: 100,
  BOUCLIER: 300,
  FORTERESSE: 600
};

export const FORMULE_SENTINELLE_LOTS: Record<FormuleSentinelle, { min: number; max: number }> = {
  SENTINELLE: { min: 1, max: 15 },
  BOUCLIER: { min: 16, max: 40 },
  FORTERESSE: { min: 41, max: 50 }
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Vérifie si une copropriété est opérationnelle
 */
export function isOperationnelle(copropriete: Copropriete): boolean {
  return copropriete.statutOnboarding === 'operationnelle';
}

/**
 * Vérifie si une copropriété est en attente de signatures de mandat
 * Gère la rétrocompatibilité avec l'ancien statut 'en_attente_confirmations'
 */
export function isEnAttenteSignatures(copropriete: Copropriete): boolean {
  return copropriete.statutOnboarding === 'en_attente_signatures' 
    || copropriete.statutOnboarding === 'en_attente_confirmations';
}

/**
 * Vérifie si une copropriété est en offre accompagnée
 */
export function isOffreAccompagnee(copropriete: Copropriete): boolean {
  return copropriete.typeOffre === 'accompagnee';
}

/**
 * Récupère l'ID du coordinateur (avec rétrocompatibilité)
 * Cherche d'abord coordinateurId, puis tombe sur administrateurId
 */
export function getCoordinateurId(copropriete: Copropriete): string | undefined {
  return copropriete.coordinateurId || copropriete.administrateurId;
}

/**
 * Récupère le nom du coordinateur (avec rétrocompatibilité)
 */
export function getCoordinateurNom(copropriete: Copropriete): string | undefined {
  return copropriete.coordinateurNom || copropriete.administrateurNom;
}

/**
 * Vérifie si un utilisateur est le coordinateur de cette copropriété
 */
export function isCoordinateur(copropriete: Copropriete, userId: string): boolean {
  const coordId = getCoordinateurId(copropriete);
  return coordId === userId;
}

/**
 * Vérifie si la GPA est encore valide
 */
export function isGPAValide(copropriete: Copropriete): boolean {
  if (!copropriete.dateExpirationGPA) return false;
  return copropriete.dateExpirationGPA.toDate() > new Date();
}

/**
 * Vérifie si la garantie biennale est encore valide
 */
export function isBiennaleValide(copropriete: Copropriete): boolean {
  if (!copropriete.dateExpirationBiennale) return false;
  return copropriete.dateExpirationBiennale.toDate() > new Date();
}

/**
 * Vérifie si la garantie décennale est encore valide
 */
export function isDecennaleValide(copropriete: Copropriete): boolean {
  if (!copropriete.dateExpirationDecennale) return false;
  return copropriete.dateExpirationDecennale.toDate() > new Date();
}

/**
 * Détermine la formule SENTINELLE selon le nombre de lots
 */
export function getFormuleSentinelle(nombreLots: number): FormuleSentinelle {
  if (nombreLots <= 15) return 'SENTINELLE';
  if (nombreLots <= 40) return 'BOUCLIER';
  return 'FORTERESSE';
}

/**
 * Calcule le quota SENTINELLE selon le nombre de lots
 */
export function getQuotaSentinelle(nombreLots: number): number {
  const formule = getFormuleSentinelle(nombreLots);
  return FORMULE_SENTINELLE_QUOTAS[formule];
}

/**
 * Vérifie si le quota SENTINELLE est épuisé
 */
export function isQuotaEpuise(copropriete: Copropriete): boolean {
  if (copropriete.sentinelleQuota === undefined || copropriete.sentinelleUsage === undefined) {
    return false;
  }
  return copropriete.sentinelleUsage >= copropriete.sentinelleQuota;
}

/**
 * Calcule le pourcentage d'utilisation du quota
 */
export function getPourcentageQuota(copropriete: Copropriete): number {
  if (!copropriete.sentinelleQuota || copropriete.sentinelleQuota === 0) return 0;
  const usage = copropriete.sentinelleUsage || 0;
  return Math.round((usage / copropriete.sentinelleQuota) * 100);
}
